'use client';

import {
  ChatPublishedPost,
  publishChatSnapshot,
  refreshChatSnapshot,
  unpublishChatSnapshot,
} from '@/app/services/chat';
import {
  publishDeepResearchSession,
  refreshDeepResearchSnapshot,
  unpublishDeepResearchSnapshot,
} from '@/app/services/deep-research';
import { Button, ButtonProps } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  CheckIcon,
  ExternalLinkIcon,
  GlobeIcon,
  GlobeOffIcon,
  LoaderCircleIcon,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

type PublishStatePayload = {
  published: boolean;
  snapshotId?: string | null;
  shareToken?: string | null;
  squareUrl?: string | null;
  publishedPost?: ChatPublishedPost | null;
};

const COVER_REFRESH_ATTEMPTS = 6;
const COVER_REFRESH_INTERVAL_MS = 2000;

function toAbsoluteUrl(path: string | null | undefined): string {
  if (!path) return '';
  if (typeof window === 'undefined') return path;
  return new URL(path, window.location.origin).toString();
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function PublishSquareButton({
  type = 'deepresearch',
  disabled,
  sid,
  snapshotId,
  isPublished,
  publishedPost,
  squareUrl,
  onPublished,
  className,
  variant,
  size,
  children,
}: {
  type?: 'search' | 'deepresearch';
  disabled: boolean;
  sid?: string;
  snapshotId?: string | null;
  isPublished: boolean;
  publishedPost?: ChatPublishedPost | null;
  squareUrl?: string | null;
  onPublished?: (payload: PublishStatePayload) => void;
  className?: string;
  variant?: ButtonProps['variant'];
  size?: ButtonProps['size'];
  children?: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localPublished, setLocalPublished] = useState(isPublished);
  const [localSnapshotId, setLocalSnapshotId] = useState(snapshotId ?? null);
  const [localSquareUrl, setLocalSquareUrl] = useState(squareUrl ?? null);
  const effectivePublished = localPublished || isPublished;
  const effectiveSnapshotId = localSnapshotId ?? snapshotId ?? null;
  const resolvedSquareUrl = useMemo(
    () => toAbsoluteUrl(localSquareUrl ?? squareUrl),
    [localSquareUrl, squareUrl],
  );

  useEffect(() => {
    setLocalPublished(isPublished);
  }, [isPublished]);

  useEffect(() => {
    setLocalSnapshotId(snapshotId ?? null);
  }, [snapshotId]);

  useEffect(() => {
    setLocalSquareUrl(squareUrl ?? null);
  }, [squareUrl]);

  const syncPublishedPost = async (
    nextSnapshotId: string,
    nextSquareUrl: string | null | undefined,
  ) => {
    for (let attempt = 0; attempt < COVER_REFRESH_ATTEMPTS; attempt += 1) {
      if (attempt > 0) {
        await sleep(COVER_REFRESH_INTERVAL_MS);
      }
      try {
        const { data } =
          type === 'deepresearch'
            ? await refreshDeepResearchSnapshot(nextSnapshotId)
            : await refreshChatSnapshot(nextSnapshotId);
        const resolvedSquareUrl = data.square_url ?? nextSquareUrl ?? null;
        if (resolvedSquareUrl) {
          setLocalSquareUrl(resolvedSquareUrl);
        }
        if (data.published_post || resolvedSquareUrl) {
          onPublished?.({
            published: true,
            snapshotId: nextSnapshotId,
            squareUrl: resolvedSquareUrl,
            publishedPost: data.published_post ?? null,
          });
        }
        if (data.published_post?.cover_url) {
          return;
        }
      } catch (error) {
        console.error(error);
        return;
      }
    }
  };

  const handlePublish = async () => {
    if (type === 'deepresearch' && !sid) {
      toast.error('缺少研究标识，请刷新后重试', {
        position: 'top-center',
      });
      return;
    }
    if (type === 'search' && !effectiveSnapshotId) {
      toast.error('缺少分享快照，请刷新后重试', {
        position: 'top-center',
      });
      return;
    }

    try {
      setIsLoading(true);
      const req = {
        anonymous: effectivePublished
          ? (publishedPost?.anonymous ?? false)
          : isAnonymous,
      };
      if (type === 'search' && effectiveSnapshotId) {
        await refreshChatSnapshot(effectiveSnapshotId);
      }
      const { data } =
        type === 'deepresearch'
          ? await publishDeepResearchSession(sid || '', req)
          : await publishChatSnapshot(effectiveSnapshotId || '', req);
      toast.success(effectivePublished ? '已更新发布' : '已发布到广场', {
        position: 'top-center',
      });
      setLocalPublished(true);
      setLocalSnapshotId(data.snapshot_id);
      setLocalSquareUrl(data.square_url);
      onPublished?.({
        published: true,
        snapshotId: data.snapshot_id,
        squareUrl: data.square_url,
        publishedPost: null,
      });
      void syncPublishedPost(data.snapshot_id, data.square_url);
    } catch (error) {
      console.error(error);
      toast.error('发布失败，请稍后重试', {
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!effectiveSnapshotId) {
      toast.error('缺少分享快照，请刷新后重试', {
        position: 'top-center',
      });
      return;
    }
    try {
      setIsLoading(true);
      if (type === 'deepresearch') {
        await unpublishDeepResearchSnapshot(effectiveSnapshotId);
      } else {
        await unpublishChatSnapshot(effectiveSnapshotId);
      }
      setLocalPublished(false);
      setLocalSquareUrl(null);
      setIsOpen(false);
      onPublished?.({
        published: false,
        snapshotId: effectiveSnapshotId,
        squareUrl: null,
        publishedPost: null,
      });
      toast.success('已取消发布到广场', {
        position: 'top-center',
      });
    } catch (error) {
      console.error(error);
      toast.error('取消发布失败，请稍后重试', {
        position: 'top-center',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        className={cn(className)}
        size={size || 'sm'}
        variant={variant || 'ghost'}
        disabled={disabled || isLoading}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(true);
        }}
      >
        {children}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg gap-0 bg-card rounded-3xl border-border/70 p-0 overflow-hidden">
          <div className="px-6 py-5">
            <DialogHeader className="gap-2 text-left">
              <DialogTitle className="text-xl">发布到分享广场</DialogTitle>
              <DialogDescription className="text-sm leading-6 text-muted-foreground">
                发布后，当前{type === 'deepresearch' ? '研究报告' : '搜索结果'}
                会以公开页面的形式展示在分享广场，任何人都可能看到，也可能被搜索引擎收录。
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-4 px-6 pb-6">
            {effectivePublished ? (
              <div className="space-y-4">
                <div className="rounded-2xl bg-muted p-4 text-sm leading-6 text-muted-foreground">
                  当前内容已发布到分享广场。可以更新为当前最新内容；取消发布后，广场页会下线，但已生成的分享链接仍可访问。
                </div>

                <div className="flex justify-end gap-2">
                  {resolvedSquareUrl ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 rounded-full px-4"
                      asChild
                    >
                      <a
                        href={resolvedSquareUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ExternalLinkIcon className="h-4 w-4" />
                        查看广场页
                      </a>
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="brand"
                    className="h-10 rounded-full px-4"
                    disabled={isLoading}
                    onClick={() => void handlePublish()}
                  >
                    {isLoading ? (
                      <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <GlobeIcon className="h-4 w-4" />
                    )}
                    更新发布
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 rounded-full px-4"
                    disabled={isLoading}
                    onClick={() => void handleUnpublish()}
                  >
                    {isLoading ? (
                      <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <GlobeOffIcon className="h-4 w-4" />
                    )}
                    取消发布
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-4 rounded-2xl bg-muted/40 p-4">
                  <div className="space-y-1">
                    <div className="text-sm font-medium text-foreground">
                      匿名发布
                    </div>
                    <div className="text-sm text-muted-foreground">
                      开启后，将隐藏你的昵称、头像等个人标识。
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isAnonymous}
                    aria-label="匿名发布"
                    className={cn(
                      'relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors',
                      isAnonymous ? 'bg-primary' : 'bg-muted-foreground/20',
                    )}
                    onClick={() => setIsAnonymous((current) => !current)}
                  >
                    <span
                      className={cn(
                        'block h-6 w-6 rounded-full bg-background shadow-sm transition-transform',
                        isAnonymous ? 'translate-x-7' : 'translate-x-1',
                      )}
                    />
                  </button>
                </div>

                <div className="rounded-2xl bg-muted/40 p-4 text-sm text-foreground">
                  <div className="flex items-start gap-3">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <span>发布到分享广场可获得 20 积分</span>
                  </div>
                  <div className="mt-3 flex items-start gap-3">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                    <span>内容被点赞可额外获得 20 积分</span>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 rounded-full px-4"
                    onClick={() => setIsOpen(false)}
                  >
                    取消
                  </Button>
                  <Button
                    type="button"
                    variant="brand"
                    className="h-10 rounded-full px-4"
                    disabled={isLoading}
                    onClick={() => void handlePublish()}
                  >
                    {isLoading ? (
                      <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                    ) : (
                      <GlobeIcon className="h-4 w-4" />
                    )}
                    确认发布
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
